import { useMemo, useState } from 'react';
import {
  Combobox,
  Dropdown,
  Field,
  MessageBar,
  MessageBarBody,
  Option,
  Text,
} from '@fluentui/react-components';
import type { GroupOption, Person, ServiceProviderOption } from '../../../api/src/shared/contract';
import { assigneesInGroup, initials } from '../../lib/directory';
import { usePaneStyles } from '../styles';

interface ProviderFieldProps {
  providers: ServiceProviderOption[];
  value: string | null;
  onChange: (providerId: string) => void;
  disabled?: boolean;
}

export function ProviderField({ providers, value, onChange, disabled }: ProviderFieldProps) {
  const selected = providers.find((p) => p.id === value);

  return (
    <Field label="Service provider" required>
      <Dropdown
        placeholder="Select a provider"
        value={selected?.name ?? ''}
        selectedOptions={value != null ? [value] : []}
        disabled={disabled || providers.length === 0}
        onOptionSelect={(_, data) => {
          if (data.optionValue) onChange(data.optionValue);
        }}
      >
        {providers.map((p) => (
          <Option key={p.id} value={p.id} text={p.name}>
            {p.name}
          </Option>
        ))}
      </Dropdown>
    </Field>
  );
}

interface GroupFieldProps {
  groups: GroupOption[];
  value: number | null;
  onChange: (groupId: number | null) => void;
}

export function GroupField({ groups, value, onChange }: GroupFieldProps) {
  const selected = groups.find((g) => g.id === value);

  return (
    <Field label="Group assignment" required>
      <Dropdown
        placeholder="Select a group"
        value={selected?.name ?? ''}
        selectedOptions={value != null ? [String(value)] : []}
        onOptionSelect={(_, data) => {
          const id = data.optionValue ? Number(data.optionValue) : NaN;
          onChange(Number.isFinite(id) ? id : null);
        }}
      >
        {groups.map((g) => (
          <Option key={g.id} value={String(g.id)} text={g.name}>
            {g.name}
          </Option>
        ))}
      </Dropdown>
    </Field>
  );
}

interface AssigneeFieldProps {
  people: Person[];
  groupId: number | null;
  value: number | null;
  clearedName: string | null;
  onChange: (assigneeId: number | null) => void;
  onDismissCleared: () => void;
}

export function AssigneeField({
  people,
  groupId,
  value,
  clearedName,
  onChange,
  onDismissCleared,
}: AssigneeFieldProps) {
  const styles = usePaneStyles();
  const [query, setQuery] = useState('');

  const options = useMemo(
    () => (groupId == null ? [] : assigneesInGroup(people, groupId)),
    [people, groupId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (p) => p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q),
    );
  }, [options, query]);

  const selected = options.find((p) => p.id === value);
  const disabled = groupId == null;

  return (
    <Field label="Assigned to">
      <Combobox
        placeholder={disabled ? 'Select a group first' : 'Unassigned'}
        disabled={disabled}
        value={selected ? selected.name : query}
        selectedOptions={value != null ? [String(value)] : []}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value != null) onChange(null);
        }}
        onOptionSelect={(_, data) => {
          if (data.optionValue === '') {
            onChange(null);
            setQuery('');
            return;
          }
          const id = data.optionValue ? Number(data.optionValue) : NaN;
          onChange(Number.isFinite(id) ? id : null);
          setQuery('');
          onDismissCleared();
        }}
        freeform
      >
        <Option value="" text="Unassigned">
          Unassigned
        </Option>
        {filtered.map((p) => (
          <Option key={p.id} value={String(p.id)} text={p.name}>
            {initials(p.name)} · {p.name}
          </Option>
        ))}
      </Combobox>
      {clearedName ? (
        <MessageBar intent="info" style={{ marginTop: 6 }}>
          <MessageBarBody>
            {clearedName} was cleared — not a member of the selected group.
          </MessageBarBody>
        </MessageBar>
      ) : disabled ? (
        <Text className={styles.hint}>
          Disabled until a group is chosen, because the list is that group’s members.
        </Text>
      ) : null}
    </Field>
  );
}
